import React from 'react';
import i18n from '../i18n';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  TextareaAutosize,
  SelectChangeEvent
} from '@mui/material';
import { FormField } from '../interfaces';

// Interface para um campo de formulário

interface RenderFormFieldProps {
  field: FormField;
  value: any;
  onChange: (name: string, value: any) => void;
  error?: string;
}

// Função principal para renderizar o campo de formulário adequado
export const renderFormField = ({
  field, value, onChange, error
}: RenderFormFieldProps, formData?: any, errors?: any) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    let fieldValue: any;
    
    if (field.type === 'number') {
      fieldValue = e.target.value === '' ? '' : Number(e.target.value);
    } else {
      fieldValue = e.target.value;
    }
    
    onChange(field.name, fieldValue);
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field.name, e.target.checked);
  };

  const commonProps = {
    id: field.name,
    label: field.label,
    required: field.required,
    error: !!error,
    helperText: error || field.helperText,
    disabled: typeof field.disabled === 'function' 
    ? field.disabled(FormData) 
    : field.disabled || false,
    fullWidth: field.fullWidth !== false,
    placeholder: field.placeholder,
    margin: 'normal' as const,
  };

  switch (field.type) {
    case 'text':
      return (
        <TextField
          {...commonProps}
          type="text"
          value={value || ''}
          onChange={handleChange}
          multiline={field.multiline}
          rows={field.rows}
        />
      );
      
    case 'number':
      return (
        <TextField
          {...commonProps}
          type="number"
          value={value ?? ''}
          onChange={handleChange}
        />
      );
      
    case 'date':
      // Usando um input de data padrão em vez do DatePicker do MUI
      return (
        <TextField
          {...commonProps}
          type="date"
          value={value || ''}
          onChange={handleChange}
          InputLabelProps={{
            shrink: true,
          }}
        />
      );
      
    case 'textarea':
      return (
        <FormControl fullWidth margin="normal" error={!!error}>
          <InputLabel shrink htmlFor={field.name}>{field.label}</InputLabel>
          <TextareaAutosize
            id={field.name}
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder}
            minRows={field.rows || 3}
            style={{ 
              width: '100%',
              marginTop: '16px',
              padding: '8px',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              borderRadius: '4px',
              borderColor: error ? '#d32f2f' : '#ccc'
            }}
            disabled={
    typeof field.disabled === 'function' 
      ? field.disabled(FormData) 
      : field.disabled || false
  }
          />
          {error && <FormHelperText>{error}</FormHelperText>}
          {field.helperText && !error && <FormHelperText>{field.helperText}</FormHelperText>}
        </FormControl>
      );
      
    case 'select':
      return (
        <FormControl fullWidth margin="normal" error={!!error} required={field.required}>
          <InputLabel id={`label-${field.name}`}>{field.label}</InputLabel>
          <Select
            labelId={`label-${field.name}`}
            id={field.name}
            value={value !== undefined && value !== null ? value : ''}
            onChange={handleChange}
            label={field.label}
            disabled={
    typeof field.disabled === 'function' 
      ? field.disabled(FormData) 
      : field.disabled || false
  }
          >
            <MenuItem value="">
              <em>{i18n.t("form.selectOption")}</em>
            </MenuItem>
            {field.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error}</FormHelperText>}
          {field.helperText && !error && <FormHelperText>{field.helperText}</FormHelperText>}
        </FormControl>
      );
      
    case 'boolean':
      return (
        <FormControl fullWidth margin="normal" error={!!error}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                onChange={handleSwitchChange}
                disabled={
    typeof field.disabled === 'function' 
      ? field.disabled(FormData) 
      : field.disabled || false
  }
                color="primary"
              />
            }
            label={field.label}
          />
          {error && <FormHelperText>{error}</FormHelperText>}
          {field.helperText && !error && <FormHelperText>{field.helperText}</FormHelperText>}
        </FormControl>
      );
      
    default:
      return null;
  }
};